/* eslint-disable no-restricted-globals */
import { isEmpty, uniqueId, compose } from 'underscore';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePopupContext, withPopupProvider, withPopupContext } from './popup-context';
import { priorityLevel } from './const';

export const useLock = ({
  key,
  priority = priorityLevel.default,
  force = false,
  isReady = true,
  autoClose = false,
}) => {
  const popupManager = usePopupContext();

  const lockKey = useMemo(() => {
    if (isEmpty(key)) {
      return uniqueId('popup-');
    }
    return uniqueId(`${key}-`);
  }, [key]);

  const [lock, setLock] = useState(false);

  const acquireLock = useCallback(async () => {
    if (force) {
      setLock(popupManager.forceAcquireLock(lockKey));
    } else {
      setLock(await popupManager.acquireLock({
        key: lockKey,
        priority,
      }));
    }
  }, [popupManager, lockKey, priority, force]);

  useEffect(() => {
    if (isReady) {
      acquireLock();
    }
  }, [isReady, acquireLock]);

  const releaseLock = useCallback(() => {
    popupManager.releaseLock(lockKey);
    setLock(false);
  }, [popupManager, lockKey]);

  useEffect(() => {
    const oldPushState = history.pushState;
    history.pushState = function pushState() /* istanbul ignore next */ {
      if (lock && autoClose) {
        releaseLock();
      }
      const ret = oldPushState.apply(this, arguments); // eslint-disable-line prefer-rest-params
      return ret;
    };
  }, [lock, releaseLock, autoClose]);

  return {
    lock,
    releaseLock,
  };
};

export const withLock = (WrappedComponent, {
  lockKey,
  priority,
  force,
  wait,
  autoClose,
} = {}) => {
  class ComponentWithLock extends React.PureComponent {
    constructor(props) {
      super(props);

      this.popupManager = props.popupManager;
      this.lockKey = props.lockKey || lockKey;
      this.priority = props.priority || priority;
      this.force = props.force || force;
      this.wait = props.wait || wait;
      this.autoClose = props.autoClose || autoClose;

      if (isEmpty(this.lockKey)) {
        this.lockKey = uniqueId('popup-');
      } else {
        this.lockKey = uniqueId(`${this.lockKey}-`);
      }

      this.state = {
        lock: false,
      };
    }

    componentDidMount() {
      this.acquireLock();

      const handleLocationChange = () => /* istanbul ignore next */ {
        if (this.state.lock && this.autoClose) {
          this.popupManager.releaseLock(this.lockKey);
          this.setState({
            lock: false,
          });
        }
      };

      const oldPushState = history.pushState;
      history.pushState = function pushState() /* istanbul ignore next */ {
        handleLocationChange();
        const ret = oldPushState.apply(this, arguments); // eslint-disable-line prefer-rest-params
        return ret;
      };
    }

    componentWillUnmount() {
      this.popupManager.releaseLock(this.lockKey);
    }

    async acquireLock() {
      let lock;
      if (this.force) {
        lock = this.popupManager.forceAcquireLock(this.lockKey);
      } else {
        lock = await this.popupManager.acquireLock({
          key: this.lockKey,
          priority: this.priority,
          wait: this.wait,
        });
      }
      this.setState({
        lock,
      });
    }

    render() {
      return this.state.lock && <WrappedComponent
        {...this.props}
      />;
    }
  }

  ComponentWithLock.propTypes = {
    popupManager: PropTypes.shape({
      acquireLock: PropTypes.func.isRequired,
      forceAcquireLock: PropTypes.func.isRequired,
      releaseLock: PropTypes.func.isRequired,
    }).isRequired,
    lockKey: PropTypes.string,
    priority: PropTypes.number,
    force: PropTypes.bool,
    wait: PropTypes.bool,
    autoClose: PropTypes.bool,
  };

  ComponentWithLock.defaultProps = {
    lockKey: '',
    priority: null,
    force: false,
    wait: false,
    autoClose: false,
  };

  return compose(
    withPopupProvider,
    withPopupContext
  )(ComponentWithLock);
};
